import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
	capitalizeWord,
	convertDfmToApex,
	toFixed,
} from '../../../utils/generalUtils';
import { ChainEnum } from '../../../swagger/apexBridgeApiService';
import {
	getEstimatedBridgeTime,
	useBridgingDurations,
} from '../../../utils/estimatedBridgeTime';

interface FeeInformationProps {
	userWalletFee: string;
	bridgeTxFee: string;
	chain: ChainEnum;
	destinationChain: ChainEnum;
}

const FeeInformation: React.FC<FeeInformationProps> = ({
	userWalletFee,
	bridgeTxFee,
	chain,
	destinationChain,
}) => {
	const bridgingDurations = useBridgingDurations();
	const estimatedTime = getEstimatedBridgeTime(
		chain,
		destinationChain,
		bridgingDurations,
	);

	return (
		<>
			<Typography
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					gap: '16px', // Add some gap for better spacing
				}}
			>
				<Box
					component="span"
					sx={{
						display: 'flex',
						alignItems: 'center',
						color: 'rgba(255,255,255,0.6)',
					}}
				>
					<Typography
						component={'span'}
						color="white"
						fontSize="13px"
					>
						User Wallet Fee
					</Typography>
					<Tooltip
						title={
							<Typography
								color={'white'}
								sx={{ fontSize: '13px' }}
							>
								This is the fee paid to process your transaction
								on the {capitalizeWord(chain)} blockchain.
								Larger transactions have higher fees.
							</Typography>
						}
						placement="right-start"
					>
						<HelpOutlineIcon
							sx={{ marginLeft: '6px', fontSize: '16px' }}
						/>
					</Tooltip>
				</Box>
				<Box
					component="span"
					sx={{ whiteSpace: 'nowrap', fontSize: '13px' }}
				>
					{BigInt(userWalletFee) > 0
						? toFixed(convertDfmToApex(userWalletFee, chain), 6)
						: '0'}{' '}
					<Typography
						component={'span'}
						color="#828282"
						fontSize="13px"
					>
						AP3X
					</Typography>
				</Box>
			</Typography>

			<Typography
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					marginTop: '8px', // Add some margin between rows
				}}
			>
				<Box
					component="span"
					sx={{
						display: 'flex',
						alignItems: 'center',
						color: 'rgba(255,255,255,0.6)',
					}}
				>
					<Typography
						component={'span'}
						color="white"
						fontSize="13px"
					>
						Bridge Transaction Fee
					</Typography>
					<Tooltip
						title={
							<Typography
								color={'white'}
								sx={{ fontSize: '13px' }}
							>
								This fee covers the bridge blockchain
								transaction costs. This fee is set to the
								predefined minimum.
							</Typography>
						}
						placement="right-start"
					>
						<HelpOutlineIcon
							sx={{ marginLeft: '6px', fontSize: '16px' }}
						/>
					</Tooltip>
				</Box>
				<Box
					component="span"
					sx={{ whiteSpace: 'nowrap', fontSize: '13px' }}
				>
					{BigInt(bridgeTxFee) > 0
						? toFixed(convertDfmToApex(bridgeTxFee, chain), 6)
						: '0'}{' '}
					<Typography
						component={'span'}
						color="#828282"
						fontSize="13px"
					>
						AP3X
					</Typography>
				</Box>
			</Typography>

			<Typography
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					marginTop: '8px',
				}}
			>
				<Box
					component="span"
					sx={{
						color: 'rgba(255,255,255,0.6)',
					}}
				>
					<Typography
						component={'span'}
						color="white"
						fontSize="13px"
					>
						Estimated time
					</Typography>
				</Box>
				<Box component="span" fontSize="13px">
					{estimatedTime}
				</Box>
			</Typography>
		</>
	);
};

export default FeeInformation;
